import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { api } from '../../../../lib/api';

type HealthState = {
    ok: boolean;
    database: string;
    hyperdrive?: boolean;
    telegramConfigured?: boolean;
    latencyMs: number;
    timestamp?: string;
    databases?: Array<{
        driver: 'd1' | 'mysql';
        available: boolean;
        latencyMs: number | null;
        error?: string;
    }>;
};

export function ServerSettingsPage() {
    const [health, setHealth] = useState<HealthState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    async function testConnection(): Promise<void> {
        setLoading(true);
        setError('');
        try {
            setHealth(await api.health());
        } catch (cause) {
            setHealth(null);
            setError(cause instanceof Error ? cause.message : 'Não foi possível testar o servidor.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void testConnection();
    }, []);

    const databaseProbe = (driver: 'd1' | 'mysql') =>
        health?.databases?.find((probe) => probe.driver === driver);

    return (
        <section className="admin-section" aria-labelledby="server-settings-title">
            <div className="admin-section-head">
                <div>
                    <span className="section-tag">INFRAESTRUTURA</span>
                    <h2 id="server-settings-title">Servidor e banco de dados</h2>
                    <p className="muted">Acompanhe a conexão ativa e teste o ambiente sem expor credenciais.</p>
                </div>
                <button type="button" className="btn btn-primary" onClick={() => void testConnection()} disabled={loading}>
                    <Icon icon={loading ? 'lucide:loader-2' : 'lucide:refresh-cw'} width="16" height="16" />
                    {loading ? 'Testando…' : 'Testar conexão'}
                </button>
            </div>

            {error && <p className="form-error" role="alert">{error}</p>}

            <div className="dash-bi-grid">
                <article className="dash-card">
                    <div className="dash-card-header">
                        <div>
                            <h3>Servidor Pages Functions</h3>
                            <p>API responsável por autenticação, CMS e operações do banco.</p>
                        </div>
                        <span className={`dash-badge-status ${health?.ok ? 'online' : 'offline'}`}>
                            {health?.ok ? 'ONLINE' : 'OFFLINE'}
                        </span>
                    </div>
                    <div className="dash-health-list">
                        <p><strong>API:</strong> {health?.ok ? 'respondendo' : 'indisponível'}</p>
                        <p><strong>Último teste:</strong> {health?.timestamp ? new Date(health.timestamp).toLocaleString('pt-BR') : '—'}</p>
                    </div>
                </article>

                <article className="dash-card">
                    <div className="dash-card-header">
                        <div>
                            <h3>Banco ativo</h3>
                            <p>Driver selecionado no ambiente do servidor.</p>
                        </div>
                        <Icon icon="lucide:database" width="22" height="22" />
                    </div>
                    <div className="dash-health-list">
                        <p><strong>Driver ativo:</strong> {health?.database?.toUpperCase() || '—'}</p>
                        <p><strong>Hyperdrive:</strong> {health?.hyperdrive ? 'ativo' : 'inativo'}</p>
                        <p><strong>Latência ativa:</strong> {health ? `${health.latencyMs} ms` : '—'}</p>
                    </div>
                </article>
            </div>

            <article className="admin-editor" style={{ marginTop: '24px' }}>
                <h3>Disponibilidade dos bancos</h3>
                <p className="muted">O servidor testa os dois motores. O driver ativo é definido no ambiente do servidor.</p>
                <div className="dash-status-list">
                    {(['d1', 'mysql'] as const).map((driver) => {
                        const probe = databaseProbe(driver);
                        return (
                            <div className="dash-status-item" key={driver}>
                                <div className="dash-status-label">
                                    <span className={`dash-dot ${probe?.available ? 'green' : 'red'}`} />
                                    <span>{driver === 'd1' ? 'Cloudflare D1' : 'MySQL / MariaDB'}</span>
                                </div>
                                <strong style={{ color: probe?.available ? '#10b981' : '#ef4444' }}>
                                    {probe?.available ? `Disponível${health?.database === driver ? ' · ativo' : ''}` : 'Indisponível'}
                                </strong>
                                <small>{probe?.available ? `${probe.latencyMs} ms` : probe?.error || 'Não testado'}</small>
                            </div>
                        );
                    })}
                </div>
            </article>

            <article className="admin-editor" style={{ marginTop: '24px' }}>
                <h3>Como alterar a conexão</h3>
                <p className="muted">
                    Por segurança, credenciais e URLs não são editáveis pelo navegador. Configure o servidor e reinicie o ambiente.
                </p>
                <div className="dash-health-list">
                    <p><strong>MySQL local:</strong> defina <code>DB_DRIVER=mysql</code> e <code>MYSQL_URL</code> em <code>.dev.vars</code>.</p>
                    <p><strong>D1 local:</strong> defina <code>DB_DRIVER=d1</code> e mantenha o binding <code>DB_D1</code>.</p>
                    <p><strong>Produção:</strong> configure bindings e secrets no Cloudflare Pages e publique novamente.</p>
                </div>
                <p className="form-info" role="status">
                    O teste acima executa uma consulta real no banco configurado e informa o resultado sem mostrar segredos.
                </p>
            </article>
        </section>
    );
}